import React, { useCallback, useEffect, useState } from 'react';
import { Box, Typography } from '@mui/material';
import { useDispatch } from 'react-redux';
import { useLocation } from 'react-router-dom';
import moment from 'moment';
import {
  getLeavesRequest,
  updateApplicationStatus,
} from '../../services/UserService';
import { getLeaveTypeList } from '../../services/LeaveService';
import { getAdditionalWfhReportees } from '../../services/AdditionalWfhQuotaService';
import KeycloakService from '../../services/KeycloakService';
import { ERROR_KEY, SHOW_NOTIFICATION, WFHAttribute } from '../../constants';
import PaginationComponent from '../../components/Pagination/Pagination';
import GenericLoader from '../../components/Loader/GenericLoader';
import LeaveStatusCardComponent from '../../components/LeaveStatusCard/LeaveStatusCardComponent';
import SeniorManagerLeaveFilters from '../../components/SeniorManagerLeave/SeniorManagerLeaveFilters';
import { NO_LEAVE_REQUESTS } from '../Manage/ManageConstants';

const defaultFilters = () => ({
  status: '',
  employeeId: '',
  leaveId: '',
  startDate: moment().subtract(30, 'days').format('YYYY-MM-DD'),
  endDate: moment().format('YYYY-MM-DD'),
});

/**
 * Senior Manager inbox for Leave requests where this user is the
 * Reporting Manager / sole approver (multi-day leave only).
 * Passes multiDayOnly=true so Manage and this inbox stay disjoint.
 */
const SeniorManagerLeaveRequestsComponent = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const currentUserId = KeycloakService.getUserDetail();

  const [requests, setRequests] = useState([]);
  const [reportees, setReportees] = useState([]);
  const [leaveTypes, setLeaveTypes] = useState([]);
  const [loader, setLoader] = useState(true);
  const [filters, setFilters] = useState(() => {
    const initial = defaultFilters();
    const xyz = new URLSearchParams(location.search).get('xyz');
    if (!xyz) return initial;
    try {
      const searchString = window.atob(xyz);
      const params = new URLSearchParams(searchString);
      return {
        ...initial,
        employeeId: params.get('applierId') || '',
        startDate: params.get('startDate') || initial.startDate,
        endDate: params.get('endDate') || initial.endDate,
        status: params.get('status') || '',
      };
    } catch {
      return initial;
    }
  });
  const [page, setPage] = useState(1);
  const [pageNo, setPageNo] = useState(0);
  const [pageSize, setPageSize] = useState(5);
  const [totalPages, setTotalPages] = useState(0);
  const [refresh, setRefresh] = useState(false);

  const notify = (type, message) => {
    dispatch({
      type: SHOW_NOTIFICATION,
      data: { open: true, type, message },
    });
  };

  const enrichLeaveName = (request, types) => {
    const leaveType = (types || []).find(
      (item) => item.id === request.applicationDTO?.leaveId,
    );
    request.leaveType = leaveType?.leaveName;
    request.leaveCategoryId = leaveType?.leaveCategoryId;
  };

  const ensureLeaveTypes = useCallback(async () => {
    if (leaveTypes.length) {
      return leaveTypes;
    }
    const year = new Date().getFullYear();
    const response = await getLeaveTypeList(year);
    const types = !response.isError ? response.data || [] : [];
    setLeaveTypes(types);
    return types;
  }, [leaveTypes]);

  const loadReportees = useCallback(async () => {
    const response = await getAdditionalWfhReportees('', 0, 200);
    if (!response.isError) {
      setReportees(response.data?.data?.content || response.data?.content || []);
    }
  }, []);

  const loadRequests = useCallback(async () => {
    if (!currentUserId) {
      setLoader(false);
      return;
    }
    setLoader(true);
    const types = await ensureLeaveTypes();
    const filterData = {
      startDate: filters.startDate,
      endDate: filters.endDate,
      employeeId: filters.employeeId,
      status: filters.status,
      leaveId: filters.leaveId,
    };
    const response = await getLeavesRequest(
      currentUserId,
      filterData,
      false,
      pageSize,
      pageNo,
      true,
    );
    setLoader(false);
    if (!response.isError) {
      const pageData = response.data?.data || {};
      const content = pageData.content || [];
      content.forEach((request) => {
        request.empName = `${request.applicationDTO?.empName || ''}`;
        request.approverList =
          request.applicationDTO?.applicationStatusDTOS || [];
        enrichLeaveName(request, types);
      });
      setRequests(content);
      setTotalPages(pageData.totalPages || 0);
    } else {
      notify(ERROR_KEY, response.message);
    }
  }, [currentUserId, filters, pageNo, pageSize, ensureLeaveTypes]);

  useEffect(() => {
    loadReportees();
  }, [loadReportees]);

  useEffect(() => {
    loadRequests();
  }, [loadRequests, refresh]);

  useEffect(() => {
    setPageNo(0);
    setPage(1);
    setRefresh((prev) => !prev);
  }, [filters]);

  const pageChange = (event, value) => {
    setPageNo(value - 1);
    setPage(value);
    setRefresh((prev) => !prev);
  };

  const handlePageSizeChange = (event) => {
    setPageSize(event.target.value);
    setPageNo(0);
    setPage(1);
    setRefresh((prev) => !prev);
  };

  const updateApplication = async (templateApplicationResponse) => {
    const response = await updateApplicationStatus(
      templateApplicationResponse,
    );
    if (!response.isError) {
      setRefresh((prev) => !prev);
    }
    notify(response.type, response.message);
    return response;
  };

  return (
    <Box className="main-content-overlay">
      <div className="main-content-div">
        <Typography variant="h6" sx={{ mb: 1, mt: 1 }}>
          Leave Requests
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Leave applications awaiting your approval as Reporting Manager.
        </Typography>

        <SeniorManagerLeaveFilters
          filters={filters}
          setFilters={setFilters}
          reportees={reportees}
          leaveTypes={leaveTypes}
        />

        {loader ? (
          <GenericLoader
            rectAttributes={WFHAttribute}
            width="100%"
            height={400}
          />
        ) : requests.length === 0 ? (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              minHeight: '40vh',
            }}
          >
            <Typography color="text.secondary">{NO_LEAVE_REQUESTS}</Typography>
          </Box>
        ) : (
          requests.map((request) => (
            <LeaveStatusCardComponent
              key={request.id}
              leave={request}
              leaveStatus={request.applicationDTO?.status}
              updateApplication={updateApplication}
              variant="manager"
            />
          ))
        )}

        {requests.length !== 0 && (
          <Box className="page-holder" sx={{ marginBottom: '10px' }}>
            <PaginationComponent
              count={totalPages}
              page={page}
              pageChange={pageChange}
              pageSize={pageSize}
              handlePageSizeChange={handlePageSizeChange}
            />
          </Box>
        )}
      </div>
    </Box>
  );
};

export default SeniorManagerLeaveRequestsComponent;
